import * as Sentry from '@sentry/nuxt'
import { isAbortError } from './utility/isAbortError'

const config = useRuntimeConfig()

// Skip Sentry initialization if SENTRY_DISABLED is set to any value
if (config.public.sentry?.disabled) {
  // Sentry is disabled via SENTRY_DISABLED environment variable
} else if (
  config.public.sentry?.dsn &&
  (process.env.NODE_ENV === 'production' ||
    process.env.NODE_ENV === 'development')
) {
  Sentry.init({
    debug: config.public.sentry?.debug || false,
    dsn: config.public.sentry.dsn,
    tunnel: '/api/sentry-tunnel',
    environment:
      config.public.sentry.config?.environment ||
      process.env.NODE_ENV ||
      'development',
    release: config.public.alfredVersion?.replace('-', '.') || 'noversion',
    sendDefaultPii: true, // Enable IP address collection
    tracesSampleRate: 0.05, // 5% sample rate for performance monitoring (budget-conscious)
    integrations: function (integrations) {
      // integrations will be all default integrations

      const toAdd = [
        Sentry.zodErrorsIntegration({
          // Zod validation error integration
        }),
        Sentry.reportingObserverIntegration(),
        // Note: feedbackIntegration() creates a floating "Report a Bug" widget
        // We use showReportDialog() programmatically instead
        // Note: Some integrations are auto-enabled in @sentry/nuxt:
        // - browserTracingIntegration (when tracesSampleRate > 0)
        // - dedupeIntegration, linkedErrorsIntegration, breadcrumbsIntegration
        // - browserApiErrorsIntegration, globalHandlersIntegration, etc.
        Sentry.vueIntegration({
          tracingOptions: {
            trackComponents: true,
            hooks: ['mount', 'update', 'unmount'],
          },
        }),
        Sentry.piniaIntegration(usePinia(), {
          attachPiniaState: false, // Disabled - exhausted Sentry attachment quota
          stateTransformer: (state) => {
            const transformedState = {
              ...state,
              calendar: {
                ...state.calendar,
                state: {
                  ...state.calendar.state,
                  cache: null,
                },
              },
              facetsStore: null,
              searchStorePinia: {
                ...state.searchStorePinia,
                state: null,
                context: null,
                searchCounts: null,
                initialCounts: null,
              },
            }

            for (const [key, value] of Object.entries(transformedState)) {
              try {
                const serializedValue = JSON.stringify(value)
                const sizeInBytes = new Blob([serializedValue]).size
                const sizeInKB = Math.round((sizeInBytes / 1024) * 100) / 100
                if (sizeInKB > 40) {
                  console.warn(`${key} is over 40KB:`, sizeInKB, 'KB')
                }
              } catch (error) {
                console.warn(
                  `Sentry.piniaIntegration stateTransformer error for ${key}`,
                )
              }
            }

            return transformedState
          },
        }),
        // Additional integrations for better error context (no extra cost):
        Sentry.extraErrorDataIntegration({
          // Captures all non-native error properties as extra data
          depth: 5, // How deep to traverse the error object
        }),
        Sentry.httpClientIntegration({
          // Captures failed HTTP requests with request/response info
          failedRequestStatusCodes: [[500, 599]], // Capture 4xx and 5xx errors
          failedRequestTargets: [/.*/], // Capture all failed requests
        }),
        Sentry.contextLinesIntegration({
          // Adds source code context to stack traces (client-side)
          frameContextLines: 7, // Number of lines before/after the error line
        }),
        Sentry.moduleMetadataIntegration({
          // Adds module/package info to stack frames for better debugging
        }),
        Sentry.feedbackIntegration({
          autoInject: false,
          colorScheme: 'light',
          enableScreenshot: false, // Disabled - exhausted Sentry attachment quota
        }),
      ]
      integrations.push(...toAdd)

      return integrations.filter(function (integration) {
        return integration.name !== 'Dedupe'
      })
    },
    normalizeDepth: 10,
    ignoreErrors: [
      // Your existing ignore patterns
      '403',
      /GSI_LOGGER/,
      /cookieyes/,
      /Unload event listeners are deprecated/,
      /Attribution Reporting is deprecated/,
      /Topics API is deprecated/,
      'AbortError',
      'The operation was aborted',
      'The user aborted a request',
      /operation.*aborted/i,
      /request.*aborted/i,
      /fetch.*aborted/i,
      /user.*aborted.*request/i,
      'Loading chunk',
      'Importing a module script failed',
      'Failed to fetch dynamically imported module',
      /ChunkLoadError/,
      /Loading CSS chunk/,
      /Failed to import dynamically/,
    ],
    beforeSend(event: any) {
      // Your existing beforeSend logic
      if (typeof window !== 'undefined' && window.preventSentryLogging) {
        return null
      }

      // Check if any stacktrace frames contain cookieyes URLs
      const hasCookieyesStack =
        event.exception?.values?.[0]?.stacktrace?.frames?.some((frame: any) =>
          frame.filename?.includes('cookieyes'),
        )

      // Check if the error message contains cookieyes
      const message = event.exception?.values?.[0]?.value || ''
      const hasCookieyesInMessage = message.includes('cookieyes')

      // If it's a cookieyes-related error, ignore it
      if (hasCookieyesStack || hasCookieyesInMessage) {
        return null
      }

      // Otherwise check other error patterns
      if (message.match(/GSI_LOGGER/i)) {
        return null
      }

      // Filter out chunk loading errors that might slip through ignoreErrors
      // These are handled by Nuxt's automatic reload via emitRouteChunkError config
      const isChunkLoadError =
        message.includes('Loading chunk') ||
        message.includes('Importing a module script failed') ||
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('ChunkLoadError') ||
        message.includes('Loading CSS chunk') ||
        message.includes('Failed to import dynamically')

      if (isChunkLoadError) {
        return null
      }

      // Filter out AbortErrors from navigation-based request cancellation
      if (isAbortError({ message, name: event.exception?.values?.[0]?.type })) {
        return null
      }

      return event
    },
  })
}
